import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  AssignmentReturn as RefundIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Registration, PaymentAttempt, registrationService } from '../services/dashboardService';
import GroupOrderChip from './GroupOrderChip';
import { paymentMethodDisplay } from '../utils/paymentMethodLabel';
import {
  paymentAttemptChipColor,
  paymentAttemptLabel,
  registrationPaymentChipColor,
  registrationPaymentLabel,
} from '../utils/registrationStatusLabel';
import { useBrandRegistrationCopy } from '../brands/registrationCopy';
import { RootState } from '../store';

type DataChangeLog = {
  id: string;
  changes: Array<{ field: 'name' | 'email' | 'phone'; from: string; to: string }>;
  note?: string | null;
  changedByName?: string | null;
  changedByEmail?: string | null;
  createdAt: string;
};

const FIELD_LABEL: Record<'name' | 'email' | 'phone', string> = {
  name: 'Nome',
  email: 'E-mail',
  phone: 'Telefone',
};

interface RegistrationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  registration: Registration | null;
  /** Chamado após solicitar/cancelar reembolso para refrescar a lista. */
  onChanged?: () => void;
}

const RegistrationDetailsModal: React.FC<RegistrationDetailsModalProps> = ({
  open,
  onClose,
  registration,
  onChanged,
}) => {
  const t = useBrandRegistrationCopy();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const refundFeatureEnabled = useSelector(
    (state: RootState) => !!state.auth.user?.parish?.features?.allowRefundRequest
  );
  const [attempts, setAttempts] = useState<PaymentAttempt[]>([]);
  const [changeLogs, setChangeLogs] = useState<DataChangeLog[]>([]);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundPassword, setRefundPassword] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState<string | null>(null);
  const [localPending, setLocalPending] = useState(registration?.pendingRefundRequest ?? null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [localName, setLocalName] = useState(registration?.participantName || '');
  const [localEmail, setLocalEmail] = useState(registration?.participantEmail || '');
  const [localPhone, setLocalPhone] = useState(registration?.formData?.phone || '');
  const [localFormData, setLocalFormData] = useState(registration?.formData || {});

  useEffect(() => {
    setLocalPending(registration?.pendingRefundRequest ?? null);
    setRefundSuccess(null);
    setRefundError(null);
    setRefundOpen(false);
    setRefundReason('');
    setRefundPassword('');
    setEditing(false);
    setEditError(null);
    setEditSuccess(null);
    setEditNote('');
    setLocalName(registration?.participantName || '');
    setLocalEmail(registration?.participantEmail || '');
    setLocalPhone(registration?.formData?.phone || '');
    setLocalFormData(registration?.formData || {});
    setEditName(registration?.participantName || '');
    setEditEmail(registration?.participantEmail || '');
    setEditPhone(registration?.formData?.phone || '');
  }, [registration?.id, registration?.pendingRefundRequest, open]);

  useEffect(() => {
    if (!open || !registration?.id) {
      setAttempts([]);
      setChangeLogs([]);
      return;
    }
    let cancelled = false;
    registrationService
      .getPaymentAttempts(registration.id)
      .then((data) => {
        if (!cancelled) setAttempts(data);
      })
      .catch(() => {
        if (!cancelled) setAttempts([]);
      });
    registrationService
      .getDataChanges(registration.id)
      .then((data) => {
        if (!cancelled) setChangeLogs(data);
      })
      .catch(() => {
        if (!cancelled) setChangeLogs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, registration?.id]);

  if (!registration) return null;

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getFieldType = (value: any): string => {
    if (Array.isArray(value)) return 'Lista';
    if (typeof value === 'boolean') return 'Sim/Não';
    if (typeof value === 'number') return 'Número';
    if (typeof value === 'object') return 'Objeto';
    return 'Texto';
  };

  const getFieldColor = (value: any): 'default' | 'primary' | 'secondary' | 'success' | 'error' => {
    if (value === null || value === undefined || value === '') return 'error';
    if (Array.isArray(value) && value.length === 0) return 'error';
    if (typeof value === 'boolean') return value ? 'success' : 'error';
    return 'success';
  };

  const formDataEntries = Object.entries(localFormData || {});
  const basicFields = ['name', 'email', 'phone'];
  const customFields = formDataEntries.filter(([key]) => !basicFields.includes(key));

  const refundBlocked = registration.refundBlocked ?? null;
  const canRequestRefund =
    refundFeatureEnabled &&
    registration.paymentStatus === 'paid' &&
    Number(registration.amountPaid || 0) > 0 &&
    !localPending &&
    !refundBlocked;

  const handleRequestRefund = async () => {
    if (!registration?.id || refundReason.trim().length < 5) {
      setRefundError('Informe o motivo (mínimo 5 caracteres).');
      return;
    }
    if (!refundPassword) {
      setRefundError('Digite sua senha para confirmar.');
      return;
    }
    setRefundBusy(true);
    setRefundError(null);
    try {
      const res = await registrationService.requestRefund(registration.id, {
        reason: refundReason.trim(),
        password: refundPassword,
      });
      setRefundSuccess(res.message || 'Solicitação enviada ao painel admin.');
      setRefundOpen(false);
      setRefundPassword('');
      setLocalPending({
        id: 'pending',
        amount: registration.amountPaid,
        reason: refundReason.trim(),
        requestedAt: new Date().toISOString(),
        status: 'pending',
      });
      onChanged?.();
    } catch (err: any) {
      setRefundError(err?.response?.data?.message || 'Erro ao solicitar reembolso.');
    } finally {
      setRefundBusy(false);
    }
  };

  const handleCancelRefundRequest = async () => {
    if (!registration?.id) return;
    setRefundBusy(true);
    setRefundError(null);
    try {
      const res = await registrationService.cancelRefundRequest(registration.id);
      setRefundSuccess(res.message || 'Solicitação cancelada.');
      setLocalPending(null);
      onChanged?.();
    } catch (err: any) {
      setRefundError(err?.response?.data?.message || 'Erro ao cancelar solicitação.');
    } finally {
      setRefundBusy(false);
    }
  };

  const startEditing = () => {
    setEditName(localName || '');
    setEditEmail(localEmail || '');
    setEditPhone(localPhone || '');
    setEditNote('');
    setEditError(null);
    setEditSuccess(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditError(null);
    setEditNote('');
  };

  const handleSaveParticipant = async () => {
    if (!registration?.id) return;
    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();
    const phone = editPhone.trim();
    if (!name) {
      setEditError('Nome não pode ficar vazio.');
      return;
    }
    if (!email || !email.includes('@')) {
      setEditError('E-mail inválido.');
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      const res = await registrationService.updateParticipant(registration.id, {
        name,
        email,
        phone,
        note: editNote.trim() || undefined,
      });
      const nextName = res.data?.participantName || name;
      const nextEmail = res.data?.participantEmail || email;
      const nextForm = res.data?.formData || {
        ...localFormData,
        name: nextName,
        email: nextEmail,
        phone,
      };
      setLocalName(nextName);
      setLocalEmail(nextEmail);
      setLocalPhone(String(nextForm.phone || phone || ''));
      setLocalFormData(nextForm);
      setEditing(false);
      setEditSuccess(res.message || 'Dados atualizados. Suporte foi notificado.');
      const logs = await registrationService.getDataChanges(registration.id).catch(() => null);
      if (logs) setChangeLogs(logs);
      onChanged?.();
    } catch (err: any) {
      setEditError(err?.response?.data?.message || 'Erro ao salvar alterações.');
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { minHeight: fullScreen ? '100%' : '70vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6">
          {t('Detalhes da Inscrição')} - {registration.confirmationCode}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 1,
            }}
          >
            <Typography variant="h6" color="primary">
              Informações Básicas
            </Typography>
            {!editing ? (
              <Button size="small" startIcon={<EditIcon />} onClick={startEditing} variant="outlined">
                Editar dados
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={cancelEditing} disabled={editBusy}>
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={editBusy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={() => void handleSaveParticipant()}
                  disabled={editBusy}
                >
                  Salvar
                </Button>
              </Box>
            )}
          </Box>

          {editSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setEditSuccess(null)}>
              {editSuccess}
            </Alert>
          )}
          {editError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}

          {editing ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome completo"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  disabled={editBusy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="E-mail"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  disabled={editBusy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  disabled={editBusy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status do Pagamento
                  </Typography>
                  <Chip
                    label={registrationPaymentLabel(registration)}
                    color={registrationPaymentChipColor(registration)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observação (opcional)"
                  placeholder="Motivo da correção — vai no log e no aviso"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  disabled={editBusy}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Nome Completo
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localName}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <GroupOrderChip count={registration.orderAttendeeCount} />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localEmail}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {localPhone || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status do Pagamento
                  </Typography>
                  <Chip
                    label={registrationPaymentLabel(registration)}
                    color={registrationPaymentChipColor(registration)}
                    size="small"
                  />
                  {localPending && (
                    <Chip
                      label="Reembolso pendente"
                      color="warning"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>

        {(refundFeatureEnabled &&
          (canRequestRefund || localPending || refundBlocked || refundSuccess || refundError)) && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Reembolso
              </Typography>
              {refundSuccess && (
                <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setRefundSuccess(null)}>
                  {refundSuccess}
                </Alert>
              )}
              {refundError && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setRefundError(null)}>
                  {refundError}
                </Alert>
              )}
              {refundBlocked ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {refundBlocked.message}
                </Alert>
              ) : localPending ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Solicitação pendente de aceite no painel admin
                  {localPending.reason ? `: “${localPending.reason}”` : '.'}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  A solicitação vai para o painel admin, que aceita e processa o estorno no gateway
                  (Pagar.me / Mercado Pago).
                </Typography>
              )}
              {canRequestRefund && !refundOpen && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RefundIcon />}
                  onClick={() => setRefundOpen(true)}
                  disabled={refundBusy}
                >
                  Solicitar reembolso
                </Button>
              )}
              {localPending && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => void handleCancelRefundRequest()}
                  disabled={refundBusy}
                >
                  {refundBusy ? <CircularProgress size={18} /> : 'Cancelar solicitação'}
                </Button>
              )}
              {refundOpen && (
                <Paper sx={{ p: 2, mt: 1.5, bgcolor: 'grey.50' }}>
                  <TextField
                    fullWidth
                    label="Motivo do reembolso *"
                    multiline
                    rows={3}
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Sua senha *"
                    type="password"
                    autoComplete="current-password"
                    value={refundPassword}
                    onChange={(e) => setRefundPassword(e.target.value)}
                    helperText="Confirme com a senha da sua conta para enviar a solicitação."
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      onClick={() => {
                        setRefundOpen(false);
                        setRefundPassword('');
                      }}
                      disabled={refundBusy}
                    >
                      Voltar
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => void handleRequestRefund()}
                      disabled={
                        refundBusy || refundReason.trim().length < 5 || !refundPassword
                      }
                    >
                      {refundBusy ? <CircularProgress size={20} color="inherit" /> : 'Confirmar e enviar'}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Informações do Evento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Evento
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {registration.eventTitle}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Ingresso
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {registration.ticketName}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Valor Pago
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {registration.amountPaid > 0 ? `R$ ${registration.amountPaid.toFixed(2)}` : 'Gratuito'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('Data da Inscrição')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {new Date(registration.createdAt).toLocaleString('pt-BR')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Histórico de edições
          </Typography>
          {changeLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma edição de dados registrada.
            </Typography>
          ) : (
            <Grid container spacing={1.5}>
              {changeLogs.map((log) => (
                <Grid item xs={12} key={log.id}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.75,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.changedByName || log.changedByEmail || 'Organizador'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(log.createdAt).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                    {log.changes.map((change, idx) => (
                      <Typography key={`${log.id}-${change.field}-${idx}`} variant="body2">
                        <strong>{FIELD_LABEL[change.field]}:</strong>{' '}
                        “{change.from || '—'}” → “{change.to || '—'}”
                      </Typography>
                    ))}
                    {log.note ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Obs.: {log.note}
                      </Typography>
                    ) : null}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Histórico de tentativas
          </Typography>
          {attempts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma tentativa de pagamento registrada.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {attempts.length} tentativa{attempts.length === 1 ? '' : 's'}
                {attempts.filter((item) => item.status === 'failed').length
                  ? ` · ${attempts.filter((item) => item.status === 'failed').length} com erro`
                  : ''}
              </Typography>
              <Grid container spacing={1.5}>
                {attempts.map((attempt) => (
                  <Grid item xs={12} key={attempt.id}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={paymentAttemptLabel(attempt.status)}
                          color={paymentAttemptChipColor(attempt.status)}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(attempt.createdAt).toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {paymentMethodDisplay(attempt.paymentMethod)}
                        {attempt.amount != null ? ` · ${Number(attempt.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}
                      </Typography>
                      {attempt.errorMessage ? (
                        <Typography variant="body2" color="error" sx={{ mt: 0.75 }}>
                          {attempt.errorMessage}
                        </Typography>
                      ) : null}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>

        {customFields.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Campos Personalizados ({customFields.length})
              </Typography>
              <Grid container spacing={2}>
                {customFields.map(([key, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, mr: 1 }}>
                          {key}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={getFieldType(value)} 
                            size="small" 
                            variant="outlined"
                            color={getFieldColor(value)}
                          />
                          <Tooltip title="Copiar valor">
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(formatValue(value))}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          wordBreak: 'break-word',
                          maxHeight: '100px',
                          overflow: 'auto'
                        }}
                      >
                        {formatValue(value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}

        {formDataEntries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Nenhum dado adicional disponível
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
        <Button 
          onClick={() => copyToClipboard(JSON.stringify(localFormData, null, 2))}
          variant="contained"
          startIcon={<CopyIcon />}
        >
          Copiar Todos os Dados
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationDetailsModal;
